import { useCallback, useEffect, useRef, useState } from "react";
import Log from "../components/Log";
import { decodeJWT } from "../utils/jwtUtils";
import { useNavigate } from "react-router-dom";
import Pagination from "../components/Pagination";
import { fetchLog } from "../services/logService";
import { useFeedback } from "../Contexts/FeedbackContext";
import { TOKEN_KEY } from "../constants/storageKey";
import { ERROR_LOGS } from "../constants/feedbacks";

export default function LogPage() {
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const { showFeedback } = useFeedback();
  const [pagination, setPagination] = useState({
    current_page: 1,
    has_next: false,
    has_previous: false,
    total_pages: 0,
  });
  const navigate = useNavigate();

  const fetchLogsAndPaginate = useCallback(
    async (page = 1) => {
      const token = localStorage.getItem(TOKEN_KEY);

      if (!token) {
        navigate("/login");
        return;
      }

      const decoded = decodeJWT(token);
      const currentTimestamp = Math.floor(Date.now() / 1000);
      const isExpired = currentTimestamp > decoded.exp;

      if (isExpired) {
        navigate("/login");
        return;
      }

      setLoading(true);

      try {
        const response = await fetchLog(page);

        if (response.data.data) {
          setLogs(response.data.data);
          setPagination(response.data.pagination);
        } else {
          setLogs(response.data);
          setPagination((prev) => ({ ...prev, current_page: page }));
        }
      } catch (error) {
        showFeedback(ERROR_LOGS, false);
      } finally {
        setLoading(false);
      }
    },
    [navigate, showFeedback]
  );

  useEffect(() => {
    fetchLogsAndPaginate(1);
  }, [fetchLogsAndPaginate]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchLogsAndPaginate(newPage);
    }
  };

  useEffect(() => {
    if (scrollRef.current && logs.length > 0) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [logs]);

  return (
    <>
      <div
        ref={scrollRef}
        className="relative top-[20vh] max-sm:top-[10vh] p-2 h-[80vh] overflow-y-auto"
      >
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Chargement...</span>
          </div>
        )}

        <div tabIndex={0} className="flex flex-col gap-1 mb-4">
          {logs && logs.length > 0
            ? logs.map((log, index) => (
                <Log
                  key={log.id || index}
                  examinateur={`${log.examiner_name} ${log.examiner_surname}`}
                  action={log.action_type}
                  eleve={`${log.student_name} ${log.student_surname}`}
                  horodatage={log.action_date}
                  note={log.note_value}
                  module={log.module}
                />
              ))
            : !loading && (
                <div className="text-center py-8 text-gray-500">
                  Aucun log trouvé
                </div>
              )}
        </div>

        {pagination.total_pages > 1 && (
          <Pagination
            pagination={pagination}
            handlePageChange={handlePageChange}
            loading={loading}
          />
        )}
      </div>
    </>
  );
}
